import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import {
	AttachmentFile,
	AttachmentFileType,
	attachmentGroupMap,
	FileExtension,
	FileId,
	FileType,
	UploadedFile,
} from "@inrev/common";
import { UseUpload, useAttachmentUpload, useFileUpload } from "@portal/api/bond-request/agent";
import {
	createAttachmentFileClassificationAccessor,
	createUploadedFileClassificationAccessor,
	useClassificationSync,
} from "@portal/api/files";
import { useFileStatusDispatch } from "@portal/providers/FileStatusProvider";
import { QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiCheck, HiOutlineExclamationCircle } from "react-icons/hi2";
import { v4 } from "uuid";
import type { AttachmentContext } from "../../analytics";
import { UploadFile } from "../../types";
import { getNameAndExtensionFromFileName } from "../../utils";
import { Modal } from "../layout/Modal";
import { cn } from "../lib/utils";
import { AttachmentTypeMultiSelect } from "./AttachmentTypeMultiSelect";
import { filterToAllowedAttachmentTypes } from "./attachment-type-utils";
import { Button } from "./Button";
import { Card } from "./Card";
import { Dropzone } from "./Dropzone";
import { FileUploadItem, PendingFileUploadItem } from "./FileUploadItem";

export type UploadProps<
	TFileType extends FileType = FileType,
	T = UploadedFile<TFileType>,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
> = {
	files: (UploadedFile<TFileType> & { createdAtMs?: number })[];
	useUpload: UseUpload<T, TFileType>;
	onUpload: (value: T) => void;
	onDelete: (id: string) => void;
	onUpdate: (value: T) => void;
	onDownload?: (id: string) => void;
	getSummaryAction?: (id: string) => (() => void) | undefined;
	onBlur?: () => void;
	enablePDFViewer?: boolean;
	baseUrl?: string;
	queryKey?: string;
	enableUpload?: boolean;
	expandedByDefault?: boolean;
	enableTypeSelection?: boolean;
	showTabs?: boolean;
	allowedTypesAndLabels: TAllowedTypesAndLabels;
	allowTypeConfirmation?: boolean;
	allowedExtensions?: FileExtension[];
	requiredTypes?: TRequired;
	typeQuestionText?: string;
	maxTypesPerFile?: number;
	subsequentFileTypeRestriction?: true;
	maxFiles?: number;
	hideChecklist?: boolean;
	showErrors?: boolean;
	allowDelete?: boolean;
	className?: string;
	analyticsContext: AttachmentContext;
	trackingPrefix: string;
};

const Upload = <
	TFileType extends FileType = FileType,
	T = UploadedFile<TFileType>[],
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
>({
	files,
	useUpload,
	onUpload,
	onDelete,
	onDownload,
	getSummaryAction,
	onBlur,
	onUpdate,
	expandedByDefault = false,
	enableTypeSelection = true,
	enablePDFViewer = true,
	baseUrl,
	queryKey,
	enableUpload = true,
	showTabs,
	allowedTypesAndLabels,
	allowedExtensions,
	requiredTypes,
	allowTypeConfirmation,
	maxTypesPerFile,
	subsequentFileTypeRestriction,
	maxFiles,
	showErrors,
	hideChecklist,
	allowDelete,
	className,
	analyticsContext,
	trackingPrefix,
}: UploadProps<TFileType, T, TAllowedTypesAndLabels, TAllowedType, TRequired>) => {
	const [filesPendingUpload, setFilesPendingUpload] = useState<UploadFile<TFileType>[]>([]);
	const [_filesPendingUpdate, setFilesPendingUpdate] = useState<UploadedFile<TFileType>[]>([]);
	const [fileIdsPendingDelete, setFileIdsPendingDelete] = useState<string[]>([]);
	const [showTypeSelection, setShowTypeSelection] = useState<boolean>(false);
	const [filesForTypeSelection, setFilesForTypeSelection] = useState<File[] | undefined>();
	const [uploadModalError, setUploadModalError] = useState<string | undefined>();
	const [tabHeight, setTabHeight] = useState<number>(0);
	const { setIsFileStatusPending } = useFileStatusDispatch();

	const allowedTypes = useMemo(() => {
		const types = Object.keys(allowedTypesAndLabels);
		if (!types.length) throw new Error();
		return types as [TFileType, ...TFileType[]];
	}, [allowedTypesAndLabels]);

	const categorizedFiles = useMemo(() => {
		const categoryMap: Record<string, (UploadedFile<TFileType> & { createdAtMs?: number })[]> = {};
		const fileMap: Array<{
			categoryLabel: string;
			files: (UploadedFile<TFileType> & { createdAtMs?: number })[];
		}> = [];
		const otherFiles: (UploadedFile<TFileType> & { createdAtMs?: number })[] = [];

		files.forEach((file) => {
			let fileCategorized = false;
			for (const [tab, typeLabelMap] of attachmentGroupMap) {
				file.types.forEach((fileType) => {
					if (typeLabelMap[fileType]) {
						if (!categoryMap[tab]) {
							categoryMap[tab] = [];
						}
						if (!categoryMap[tab].includes(file)) {
							categoryMap[tab].push(file);
							fileCategorized = true;
						}
					}
				});
			}
			if (!fileCategorized) {
				otherFiles.push(file);
			}
		});

		fileMap.push({ categoryLabel: "All", files });
		// Order the tabs based on the order of the attachmentGroupMap
		for (const [tab, _typeLabelMap] of attachmentGroupMap) {
			if (categoryMap[tab]) {
				fileMap.push({ categoryLabel: tab, files: categoryMap[tab] });
			}
		}
		// Add Other category if there are any files that don't fit into a category
		if (otherFiles.length && otherFiles.length !== files.length) {
			fileMap.push({ categoryLabel: "Other", files: otherFiles });
		}

		return fileMap;
	}, [files]);

	const showAttachmentGroupTabs = () => {
		if (!showTabs) {
			return false;
		}
		if (!categorizedFiles.length || !categorizedFiles[0].files.length) {
			return false;
		}
		if (
			categorizedFiles.length === 2 &&
			categorizedFiles[0].categoryLabel === "All" &&
			categorizedFiles[1].categoryLabel === "Other"
		) {
			return false;
		}
		return true;
	};

	const atLeastOneSelectedType = (
		selectedTypes: TFileType[],
	): selectedTypes is [TFileType, ...TFileType[]] => {
		// always return true if types are optional
		if (!requiredTypes || requiredTypes.length === 0) return true;
		return selectedTypes.length > 0;
	};

	const requiredTypesFulfilled = useMemo(() => {
		if (!requiredTypes?.length) return true;
		return requiredTypes.every((requiredType) =>
			files.some((file) => file.types?.includes(requiredType)),
		);
	}, [requiredTypes, files]);

	const { uploadFn, updateFn, deleteFn, fileLoading } = useUpload({
		onUploadSuccess: useCallback(
			(data, args) => {
				setFilesPendingUpload((filesPendingUpload) =>
					filesPendingUpload.filter(
						(file) => !args.fileUploads.some((upload) => file.id === upload.pendingFile.id),
					),
				);
				onUpload(data);
			},
			[setFilesPendingUpload, onUpload],
		),
		onUploadError: (error, args) => {
			console.error(error);
			setFilesPendingUpload((filesPendingUpload) =>
				filesPendingUpload.filter(
					(file) => !args.fileUploads.some((upload) => file.id === upload.pendingFile.id),
				),
			);
		},
		onUpdateSuccess: (data, args) => {
			setFilesPendingUpload((filesPendingUpload) =>
				filesPendingUpload.filter((file) => file.id !== args.pendingFile.id),
			);
			onUpdate(data);
		},
		onUpdateError: (error, args) => {
			console.error(error);
			setFilesPendingUpdate((filesPendingUpdate) =>
				filesPendingUpdate?.filter((file) => file.id !== args.pendingFile.id),
			);
		},
		onDeleteSuccess: (args) => {
			onDelete(args.id);
			setFileIdsPendingDelete((fileIdsPendingDelete) =>
				fileIdsPendingDelete.filter((id) => id !== args.id),
			);
			onBlur?.();
		},
		onDeleteError: (error, args) => {
			console.error(error);
			setFileIdsPendingDelete((fileIdsPendingDelete) =>
				fileIdsPendingDelete.filter((id) => id !== args.id),
			);
		},
	});

	const handleUpload = useCallback((dropZoneFiles: File[]) => {
		if (allowedTypes.length === 1) {
			const fileUploads = dropZoneFiles.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: { id: v4() as FileId, name: fileName, extension, types: allowedTypes },
				};
			});
			setFilesPendingUpload((filesPendingUpload) => [
				...fileUploads.map((upload) => upload.pendingFile),
				...filesPendingUpload,
			]);
			uploadFn({ fileUploads });
		} else if (subsequentFileTypeRestriction && (files.length || filesPendingUpload.length)) {
			const fileUploads = dropZoneFiles.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: {
						id: v4() as FileId,
						name: fileName,
						extension,
						types: files[0]?.types ?? filesPendingUpload[0]?.types,
					},
				};
			});
			setFilesPendingUpload((filesPendingUpload) => [
				...fileUploads.map((upload) => upload.pendingFile),
				...filesPendingUpload,
			]);
			uploadFn({ fileUploads });
		} else if (!!requiredTypes) {
			setFilesForTypeSelection(dropZoneFiles);
			setShowTypeSelection(true);
		} else {
			const fileUploads = dropZoneFiles.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: { id: v4() as FileId, name: fileName, extension, types: [] },
				};
			});
			setFilesPendingUpload((filesPendingUpload) => [
				...fileUploads.map((upload) => upload.pendingFile),
				...filesPendingUpload,
			]);
			uploadFn({ fileUploads });
		}
	}, []);

	useEffect(() => {
		if (fileLoading) {
			setIsFileStatusPending(true);
			return () => setIsFileStatusPending(false);
		}
	}, [fileLoading, setIsFileStatusPending]);

	const handleDelete = (id: string) => {
		if (allowDelete) {
			setFileIdsPendingDelete((fileIdsPendingDelete) => [...fileIdsPendingDelete, id]);
			deleteFn({ id });
		}
	};

	const handleTypeConfirmation = (file: UploadedFile<TFileType>) => {
		try {
			const updateArgs = {
				pendingFile: {
					...file,
					types: filterToAllowedAttachmentTypes(file.types ?? [], allowedTypes),
				},
			};
			updateFn(updateArgs);
		} catch (error) {
			console.error(error);
		}
	};

	const handleTypeConfirmationCancel = (file: UploadedFile<TFileType>) => {
		try {
			const updateArgs = {
				pendingFile: {
					...file,
					types: [],
				},
			};
			updateFn(updateArgs);
		} catch (error) {
			console.error(error);
		}
	};

	const handleUpdateFileTypes = (file: UploadedFile<TFileType>, newTypes: TFileType[]) => {
		try {
			if (requiredTypes && !atLeastOneSelectedType(newTypes))
				throw new Error("Please select at least one type");
			const updateArgs = {
				pendingFile: {
					...file,
					types: filterToAllowedAttachmentTypes(newTypes, allowedTypes),
				},
			};
			updateFn(updateArgs);
		} catch (error) {
			if (error instanceof Error) setUploadModalError(error.message);
		}
	};

	const handleUploadTypeSelection = (newTypes: TFileType[]) => {
		try {
			if (requiredTypes && !atLeastOneSelectedType(newTypes))
				throw new Error("Please select at least one type");
			if (!filesForTypeSelection) throw new Error("No file to upload");
			const fileUploads = filesForTypeSelection.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: {
						id: v4() as FileId,
						name: fileName,
						extension,
						types: newTypes,
					},
				};
			});
			setFilesPendingUpload((filesPendingUpload) => [
				...filesPendingUpload,
				...fileUploads.map((upload) => upload.pendingFile),
			]);
			uploadFn({ fileUploads });
		} catch (error) {
			if (error instanceof Error) setUploadModalError(error.message);
		}
	};

	const onBlurRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (onBlur) {
			onBlurRef.current = onBlur;
		}
	}, [onBlur]);

	useEffect(() => {
		return () => {
			onBlurRef.current = null;
		};
	}, []);

	useEffect(() => {
		onBlurRef.current?.();
	}, [files]);

	return (
		<div className={cn("relative flex flex-col space-y-[24px] w-full", className)}>
			{requiredTypes && !!requiredTypes.length && !hideChecklist && (
				<div
					className={cn(
						"relative flex-1 flex flex-col space-y-[5px] pl-[20px] after:absolute after:w-[3px] after:rounded-full after:bg-gray-100 after:top-0 after:bottom-0 after:left-0",
						showErrors && !requiredTypesFulfilled ? "after:bg-red-100" : undefined,
					)}
				>
					{requiredTypes.map((type) => {
						const typeIncluded = files.some((file) => file.types.includes(type));
						return (
							<div
								key={type}
								className={cn(
									"flex items-center space-x-[10px] text-[15px]",
									typeIncluded ? "opacity-100" : "opacity-70",
									showErrors && !typeIncluded ? "text-red-500 opacity-100" : undefined,
								)}
							>
								<div
									className={cn(
										"relative aspect-square h-[14px] w-[14px] rounded-full border border-gray-400 bg-white focus:outline-none flex items-center justify-center",
										showErrors && !typeIncluded ? "border-red-500" : undefined,
									)}
								>
									{typeIncluded && (
										<div className="flex items-center justify-center absolute top-[-1px] left-[-1px] aspect-square h-[14px] w-[14px] rounded-full bg-brand-accent ml-[-1] mt-[-1]">
											<HiCheck className="text-[8px] text-inverse stroke-[3px]" />
										</div>
									)}
								</div>
								<div>{allowedTypesAndLabels[type]}</div>
							</div>
						);
					})}
				</div>
			)}
			<div className="w-full flex flex-col space-y-[10px]">
				{showErrors &&
					!requiredTypesFulfilled &&
					hideChecklist &&
					requiredTypes &&
					!!requiredTypes.length && <div className="text-[13px] text-red-500">Required</div>}

				{(!!files.length || !!filesPendingUpload.length) && (
					<>
						{showAttachmentGroupTabs() ? (
							<TabGroup vertical className="flex flex-row gap-[44px]">
								<TabList
									as="div"
									className="flex flex-col w-[150px] h-fit text-[13px] cursor-pointer"
								>
									{categorizedFiles.map((tabGroup, idx) => (
										<Tab
											as="div"
											key={`tab-${idx}`}
											className={`h-[40px] py-[9px] rounded-r-[3px] text-tertiary/90 data-[selected]:font-[475] data-[selected]:text-gray-800 hover:data-[selected]:text-gray-800 data-[selected]:bg-gray-200/55 hover:data-[selected]:bg-gray-200/55 border-l-[3px] data-[selected]:border-l-gray-300/60 cursor-pointer hover:text-secondary/95 px-3 flex items-center justify-start outline-none ring-0`}
										>
											{tabGroup.categoryLabel}
										</Tab>
									))}
								</TabList>
								<TabPanels className="flex-1">
									{enableUpload &&
										(maxFiles === undefined ||
											files.length + filesPendingUpload.length < maxFiles) && (
											<>
												<Dropzone
													allowedExtensions={allowedExtensions}
													onSuccess={handleUpload}
													trackingId={`${trackingPrefix}-dropzone`}
												/>
												<div className="mb-4" />
											</>
										)}
									{categorizedFiles?.map((fileSet, idx) => (
										<AttachmentItemTabPanel
											key={`tab-panel-${idx}`}
											tabHeight={tabHeight}
											files={fileSet.files}
											currentTab={idx}
											baseUrl={baseUrl}
											queryKey={queryKey}
											allowedTypesAndLabels={allowedTypesAndLabels}
											allowDelete={allowDelete}
											expandedByDefault={expandedByDefault}
											maxTypesPerFile={maxTypesPerFile}
											enableTypeSelection={enableTypeSelection}
											allowTypeConfirmation={allowTypeConfirmation}
											fileIdsPendingDelete={fileIdsPendingDelete}
											filesPendingUpload={filesPendingUpload}
											onSave={handleUpdateFileTypes}
											handleTypeConfirmation={handleTypeConfirmation}
											handleTypeConfirmationCancel={handleTypeConfirmationCancel}
											setTabHeight={setTabHeight}
											handleDelete={handleDelete}
											onDownload={onDownload}
											analyticsContext={analyticsContext}
										/>
									))}
								</TabPanels>
							</TabGroup>
						) : (
							<>
								{files.map((file) => (
									<FileUploadItem
										key={file.id}
										{...file}
										baseUrl={baseUrl}
										queryKey={queryKey}
										typeLabelMap={allowedTypesAndLabels}
										allowDelete={allowDelete}
										onSave={handleUpdateFileTypes}
										enablePDFViewer={enablePDFViewer}
										onTypeConfirmation={() => {
											handleTypeConfirmation(file);
										}}
										onTypeConfirmationCancel={() => {
											handleTypeConfirmationCancel(file);
										}}
										expandedByDefault={expandedByDefault}
										maxTypesPerFile={maxTypesPerFile}
										enableTypeSelection={enableTypeSelection}
										allowTypeConfirmation={allowTypeConfirmation}
										onDelete={handleDelete}
										deleting={fileIdsPendingDelete.includes(file.id)}
										onDownload={onDownload}
										onViewSummary={getSummaryAction?.(file.id)}
										analyticsContext={analyticsContext}
									/>
								))}
								{filesPendingUpload.map((file) => (
									<PendingFileUploadItem
										key={file.id}
										{...file}
										typeLabelMap={allowedTypesAndLabels}
									/>
								))}
							</>
						)}
					</>
				)}
				{enableUpload &&
					(maxFiles === undefined || files.length + filesPendingUpload.length < maxFiles) &&
					!showAttachmentGroupTabs() && (
						<Dropzone
							allowedExtensions={allowedExtensions}
							onSuccess={handleUpload}
							condensed={!!files.length || !!filesPendingUpload.length}
							trackingId={`${trackingPrefix}-dropzone`}
						/>
					)}
			</div>
			{showTypeSelection && (
				<AttachmentTypeMultiSelect
					availableTypes={allowedTypes}
					initialTypes={[]}
					selectOne={maxTypesPerFile === 1}
					expandedByDefault={expandedByDefault}
					onSave={handleUploadTypeSelection}
					title="Select File Type"
					close={() => setShowTypeSelection(false)}
					onBlur={onBlur}
				/>
			)}
			{uploadModalError && (
				<Modal onClickOutside={() => setUploadModalError(undefined)}>
					<Card className="flex flex-col items-center gap-y-[24px] p-[24px] w-[500px] max-w-[500px]">
						<div className="flex space-x-[28px] w-full px-[28px] py-[22px] border border-opacity-40 border-red-500 bg-red-50 rounded">
							<HiOutlineExclamationCircle className="text-[32px] stroke-[1.75] text-red-600/90 shrink-0 mt-[3px]" />
							<div className="text-red-600/90 flex flex-col space-y-[10px]">
								<span className="text-[20px] font-semibold">
									<p>Error</p>
								</span>
								<span className="text-[15px] font-medium">
									<p>{uploadModalError}</p>
								</span>
							</div>
						</div>
						<Button
							onClick={() => setUploadModalError(undefined)}
							color="secondary"
							filled
							className="w-1/2"
						>
							OK
						</Button>
					</Card>
				</Modal>
			)}
		</div>
	);
};

export type FileUploadProps<
	TFileType extends FileType = FileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
> = Omit<
	UploadProps<TFileType, UploadedFile<TFileType>, TAllowedTypesAndLabels, TAllowedType, TRequired>,
	"files" | "useUpload" | "onUpload" | "onDelete" | "onUpdate"
> & {
	value: UploadedFile<TFileType>[];
	onChange: (value: UploadedFile<TFileType>[]) => void;
};

export const FileUpload = <
	TFileType extends FileType = FileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
>(
	props: FileUploadProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>,
) => {
	const { value, onChange, ...rest } = props;
	const onUpload = (uploadedFiles: UploadedFile<TFileType>[]) =>
		onChange([...value, ...uploadedFiles]);
	const onDelete = (id: string) => onChange(value.filter((file) => file.id !== id));
	const onUpdate = (file: UploadedFile<TFileType>[]) => {
		onChange(value.map((existing) => (existing.id === file[0].id ? file[0] : existing)));
	};

	const classificationWatchers = useClassificationSync(
		value,
		onChange,
		createUploadedFileClassificationAccessor<UploadedFile<TFileType>>(),
	);

	return (
		<>
			<Upload
				{...rest}
				files={value}
				onUpload={onUpload}
				onDelete={onDelete}
				onUpdate={onUpdate}
				useUpload={useFileUpload}
			/>
			{classificationWatchers}
		</>
	);
};

export type AttachmentUploadProps<
	TFileType extends AttachmentFileType = AttachmentFileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
> = Omit<
	UploadProps<
		TFileType,
		AttachmentFile<TFileType>,
		TAllowedTypesAndLabels,
		TAllowedType,
		TRequired
	>,
	"files" | "useUpload" | "onUpload" | "onDelete" | "onUpdate" | "onConfirm"
> & {
	value: AttachmentFile<TFileType>[];
	onChange: (value: AttachmentFile<TFileType>[]) => void;
	onDownload?: (id: string) => void;
	onUpdate?: (value: AttachmentFile<TFileType>) => void;
	url: string;
	invalidateQueryKeys?: QueryKey[];
	filter?: (attachment: AttachmentFile<TFileType>) => boolean;
};

export const AttachmentUpload = <
	TFileType extends AttachmentFileType = AttachmentFileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
>(
	props: AttachmentUploadProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>,
) => {
	const { value, onChange, url, ...rest } = props;
	const onUpload = (attachments: AttachmentFile<TFileType>[]) =>
		onChange([...value, ...attachments]);
	const onDelete = (id: string) => onChange(value.filter((attachment) => attachment.id !== id));
	const onUpdate = (attachments: AttachmentFile<TFileType>[]) =>
		onChange(
			value.map((existing) => (existing.id === attachments[0].id ? attachments[0] : existing)),
		);
	const attachmentFiles = useMemo(
		() =>
			value.filter(props.filter ?? (() => true)).map((attachment) => ({
				...attachment.file,
				id: attachment.id as unknown as FileId,
				createdAtMs: attachment.createdAtMs,
			})),
		[value, props.filter],
	);

	const classificationWatchers = useClassificationSync(
		value,
		onChange,
		createAttachmentFileClassificationAccessor<AttachmentFile<TFileType>>(),
	);

	const useUploadHook = (callbacks: Parameters<typeof useAttachmentUpload<TFileType>>[1]) =>
		useAttachmentUpload<TFileType>(url, callbacks, props.invalidateQueryKeys);

	return (
		<>
			<Upload
				{...rest}
				files={attachmentFiles}
				onUpload={onUpload}
				onDelete={onDelete}
				onUpdate={onUpdate}
				useUpload={useUploadHook}
				onDownload={props.onDownload}
			/>
			{classificationWatchers}
		</>
	);
};

const AttachmentItemTabPanel = <
	TFileType extends FileType = FileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
>({
	tabHeight,
	files,
	allowedTypesAndLabels,
	allowDelete,
	expandedByDefault,
	maxTypesPerFile,
	enableTypeSelection,
	allowTypeConfirmation,
	fileIdsPendingDelete,
	filesPendingUpload,
	currentTab,
	baseUrl,
	queryKey,
	handleTypeConfirmation,
	handleTypeConfirmationCancel,
	handleDelete,
	onSave,
	onDownload,
	setTabHeight,
	analyticsContext,
}: {
	tabHeight: number;
	files: (UploadedFile<TFileType> & { createdAtMs?: number })[];
	itemIdx?: number;
	allowedTypesAndLabels: TAllowedTypesAndLabels;
	allowDelete?: boolean;
	expandedByDefault?: boolean;
	maxTypesPerFile?: number;
	enableTypeSelection?: boolean;
	allowTypeConfirmation?: boolean;
	fileIdsPendingDelete: string[];
	filesPendingUpload: UploadFile<TFileType>[];
	currentTab: number;
	baseUrl?: string;
	queryKey?: string;
	handleTypeConfirmation?: (file: UploadedFile<TFileType>) => void;
	handleTypeConfirmationCancel?: (file: UploadedFile<TFileType>) => void;
	handleDelete: (id: string) => void;
	onSave: (file: UploadedFile<TFileType>, newTypes: TFileType[]) => void;
	onDownload?: (id: string) => void;
	setTabHeight: (height: number) => void;
	analyticsContext: AttachmentContext;
}) => {
	const tabRef = useRef<HTMLDivElement>(null); // used so when changing tabs the height doesn't move

	useEffect(() => {
		if (tabRef.current) {
			if (tabRef.current.clientHeight > tabHeight) {
				setTabHeight(tabRef.current.clientHeight);
			}
		}
	}, [tabRef]);

	return (
		<TabPanel
			as="div"
			ref={currentTab === 0 ? tabRef : null}
			className={"min-h-fit"}
			style={tabHeight ? { height: tabHeight } : {}}
		>
			<div className="w-full. max-w-full flex flex-col space-y-[10px] cursor-default">
				{filesPendingUpload.map((file) => (
					<PendingFileUploadItem key={file.id} {...file} typeLabelMap={allowedTypesAndLabels} />
				))}
				{files
					.sort((a, b) => (a.createdAtMs && b.createdAtMs ? b.createdAtMs - a.createdAtMs : 0))
					.map((file) => (
						<FileUploadItem
							key={file.id}
							{...file}
							createdAtMs={file.createdAtMs}
							baseUrl={baseUrl}
							queryKey={queryKey}
							typeLabelMap={allowedTypesAndLabels}
							allowDelete={allowDelete}
							onSave={onSave}
							onTypeConfirmation={() => {
								!!handleTypeConfirmation && handleTypeConfirmation(file);
							}}
							onTypeConfirmationCancel={() => {
								!!handleTypeConfirmationCancel && handleTypeConfirmationCancel(file);
							}}
							expandedByDefault={expandedByDefault}
							maxTypesPerFile={maxTypesPerFile}
							enableTypeSelection={enableTypeSelection}
							allowTypeConfirmation={allowTypeConfirmation}
							onDelete={handleDelete}
							deleting={fileIdsPendingDelete.includes(file.id)}
							onDownload={onDownload}
							analyticsContext={analyticsContext}
						/>
					))}
			</div>
		</TabPanel>
	);
};
